/**
 * KAN-740: подстановка прокси справочника по префиксу метки в формах мастера
 * подключения (код, архив, сессия: все через ConnectCommonFields).
 * Пока пользователь сам не трогал прокси (выбор из справочника, "ввести
 * вручную", поля), смена префикса меняет подстановку; после ручного выбора
 * подстановка отключается до закрытия формы. Выбор прокси: pickProxyForPrefix.
 * Подстановка делается один раз на код префикса: обновление справочника
 * (refetch, фокус окна) уже подставленный прокси не меняет.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTelegramProxies } from '@/hooks/useTelegramProxies';
import { noProxyForPrefixHint, pickProxyForPrefix } from '@/lib/proxyAutoPick';
import type { CommonValues } from '@/lib/telegramConnect';

interface Options {
  values: Pick<CommonValues, 'labelMode' | 'labelPrefix'>;
  /** Подставить proxyId ("" - очистить выбор из справочника). */
  onPick: (proxyId: string) => void;
  /** false: подстановка выключена (например, переподключение). */
  enabled?: boolean;
}

interface Result {
  /** "Нет рабочего прокси для UZ" или null. */
  hint: string | null;
  /** Пользователь сам изменил прокси: больше не подставлять. */
  markTouched: () => void;
}

/** Код ещё не обрабатывался (отличается от любого кода, включая ""). */
const NOT_HANDLED = Symbol('not-handled');

export function useProxyAutoPick({ values, onPick, enabled = true }: Options): Result {
  const { data } = useTelegramProxies(enabled);
  const [touched, setTouched] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;
  const handledCode = useRef<string | typeof NOT_HANDLED>(NOT_HANDLED);

  const code = values.labelMode === 'prefix' ? values.labelPrefix.trim() : '';
  const active = enabled && !touched;

  useEffect(() => {
    // данные ещё не пришли: код не помечаем, подставим, когда придут
    if (!active || !data || handledCode.current === code) return;
    handledCode.current = code;
    const picked = pickProxyForPrefix(data, code);
    onPickRef.current(picked ? String(picked.id) : '');
    setHint(code && !picked ? noProxyForPrefixHint(code) : null);
  }, [active, data, code]);

  const markTouched = useCallback(() => setTouched(true), []);
  return { hint: active ? hint : null, markTouched };
}
